import { Pipe, PipeTransform, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Conversation, Participant, ParticipantType } from '@vendasta/conversation';
import { Observable, map } from 'rxjs';
import { formatUnreadConversationsCount, toFirestoreId } from '../../core/src/lib/conversation-utils';
import { ConversationService } from '../../core/src/lib/state/conversation.service';
import { ConversationBadge, ConversationDetail } from '../../core/src/lib/interface/conversation.interface';
import { CONVERSATION_HOST_APP_INTERFACE_TOKEN, CONVERSATION_ROUTES_TOKEN } from '../../core/src/lib/tokens';
import { ViewMode } from '../../core/src/lib/types';
import { ViewModeService } from '../../core/src/lib/view-mode.service';
import {
  getAcceptFileTypes,
  getMaxFileSize,
} from '../../ui/src/pages/inbox-chat/components/inbox-upload-file/inbox-upload.service';
import { ChatSourceId } from '@vendasta/galaxy/chat-composer/src/chat-composer.component';

// Determine the conversation URL route
@Pipe({
  name: 'conversationRoute',
  standalone: false,
})
export class ConversationRoutePipe implements PipeTransform {
  private readonly routes = toSignal(inject(CONVERSATION_ROUTES_TOKEN));

  constructor(private viewModeService: ViewModeService) {}

  transform(
    conversationDetail: ConversationDetail,
    conversationPath?: string,
    featureFlagFullscreenInboxPC?: boolean | null,
    viewMode: ViewMode = 'modal',
  ): string | [string, object] | any[] {
    const firestoreId = toFirestoreId(conversationDetail?.conversation?.conversationId);
    if (this.routes()?.useModal) {
      if (featureFlagFullscreenInboxPC) {
        return `${this.routes()?.root}/inbox/conversations/${firestoreId}`;
      }
      return this.viewModeService.routerCommands(viewMode, firestoreId);
    }

    if (conversationPath) {
      return `${conversationPath}/${firestoreId}`;
    }
    return `${this.routes()?.root}/inbox/channel/${firestoreId}`;
  }
}

@Pipe({
  name: 'conversationBadge',
  standalone: false,
})
export class ConversationBadgePipe implements PipeTransform {
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);

  private getConversationBadge(conversation: ConversationDetail): ConversationBadge[] {
    const recipient = this.hostAppInterface.getRecipient(conversation);
    return (Array.isArray(recipient) ? recipient : [recipient])
      .map((subjectParticipant) =>
        !subjectParticipant
          ? null
          : this.getConversationBadgeByParticipant(
              subjectParticipant,
              this.hostAppInterface.isRecipientInternalInfoDeleted(conversation),
            ),
      )
      .filter((badge): badge is ConversationBadge => !!badge);
  }

  /**
   * TODO - Add more cases to make the badge work in business app && vendor center
   * get badge for Participants
   * @param {SubjectParticipant} participant - the current Participant
   * @param {boolean} deleted - status of deletion
   */
  private getConversationBadgeByParticipant(participant: Participant, deleted: boolean): ConversationBadge | null {
    if (deleted) {
      return { participantType: 'INBOX.LABELS.INACTIVE', badgeColor: 'grey' };
    }
    switch (participant.participantType) {
      case ParticipantType.PARTICIPANT_TYPE_VENDOR:
        return { participantType: 'INBOX.LABELS.VENDOR', badgeColor: 'purple' };
      case ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP:
        return { participantType: 'INBOX.LABELS.ACCOUNT', badgeColor: 'blue' };
      case ParticipantType.PARTICIPANT_TYPE_CUSTOMER:
        return { participantType: 'INBOX.LABELS.CONTACT', badgeColor: 'blue' };
      case ParticipantType.PARTICIPANT_TYPE_PARTNER:
        return { participantType: 'INBOX.LABELS.PARTNER', badgeColor: 'green' };
      case ParticipantType.PARTICIPANT_TYPE_OPENAI_BOT:
        return { participantType: 'INBOX.LABELS.APP', badgeColor: 'yellow' };
    }

    return null;
  }

  transform(conversationDetail: ConversationDetail): ConversationBadge[] {
    return this.getConversationBadge(conversationDetail);
  }
}

@Pipe({
  name: 'conversationUnseen',
  standalone: false,
})
export class ConversationUnseenPipe implements PipeTransform {
  constructor(private conversationService: ConversationService) {}
  transform(conversation: Conversation): Observable<boolean> {
    return this.conversationService.conversationUnseen(conversation);
  }
}

@Pipe({
  name: 'conversationYourExpert',
  standalone: false,
})
export class ConversationYourExpertPipe implements PipeTransform {
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);

  transform(conversationDetail: ConversationDetail): boolean {
    return this.hostAppInterface.isYourExpert(conversationDetail);
  }
}

@Pipe({
  name: 'unreadConversationsCount',
  standalone: false,
})
export class UnreadConversationsCountPipe implements PipeTransform {
  constructor(private conversationService: ConversationService) {}

  transform(viewId: string): Observable<string> {
    return this.conversationService
      .getUnreadConversationsCount(viewId)
      .pipe(map((count) => formatUnreadConversationsCount(count)));
  }
}

@Pipe({
  name: 'maxFileSize',
  standalone: false,
})
export class MaxFileSize implements PipeTransform {
  transform(chatSourceId?: ChatSourceId): number {
    return getMaxFileSize(chatSourceId);
  }
}

@Pipe({
  name: 'acceptedFileType',
  standalone: false,
})
export class AcceptedFileType implements PipeTransform {
  transform(chatSourceId?: ChatSourceId): string {
    return getAcceptFileTypes(chatSourceId);
  }
}
